import { useEffect, useMemo, useRef, useState } from "react";
import { Gem, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { getErrorMessage } from "@/lib/api-error";
import { usePremiumDeals } from "@/hooks/use-premium-deals";
import type { PremiumDeal } from "@/api/org-billing-api";

interface DowngradeDealSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId?: string;
  targetPlanName: string;
  newCap: number;
  isSubmitting: boolean;
  onConfirm: (keepApplicationIds: string[]) => void | Promise<void>;
}

const byRecencyDesc = (a: PremiumDeal, b: PremiumDeal) => {
  const ta = a.last_activity_at ? Date.parse(a.last_activity_at) : 0;
  const tb = b.last_activity_at ? Date.parse(b.last_activity_at) : 0;
  return tb - ta;
};

export function DowngradeDealSelectionModal({
  open,
  onOpenChange,
  orgId,
  targetPlanName,
  newCap,
  isSubmitting,
  onConfirm,
}: DowngradeDealSelectionModalProps) {
  const { deals, isLoading, isError, error, refetch } = usePremiumDeals(
    orgId,
    open
  );
  const [keep, setKeep] = useState<Set<string>>(new Set());
  const initializedRef = useRef(false);

  const sortedDeals = useMemo(() => [...deals].sort(byRecencyDesc), [deals]);

  // Pre-select the most-recently-active deals up to the new cap when the modal
  // opens, then re-arm on close so a re-open starts from a fresh default.
  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      return;
    }
    if (initializedRef.current || sortedDeals.length === 0) return;
    setKeep(new Set(sortedDeals.slice(0, newCap).map((d) => d.application_id)));
    initializedRef.current = true;
  }, [open, sortedDeals, newCap]);

  const atCap = keep.size >= newCap;
  const demotedCount = Math.max(sortedDeals.length - keep.size, 0);

  const toggle = (id: string) => {
    setKeep((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < newCap) next.add(id);
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Choose which deals to keep</DialogTitle>
          <DialogDescription>
            {targetPlanName} allows {newCap} premium deal
            {newCap === 1 ? "" : "s"}. Choose up to {newCap} to keep — the rest
            stay in your workspace and lose premium when your plan changes.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
            <p className="text-sm text-destructive">
              {getErrorMessage(error, "Couldn't load your premium deals.")}
            </p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : sortedDeals.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No premium deals found.
          </p>
        ) : (
          <ScrollArea className="max-h-[320px] pr-3">
            <ul className="space-y-1">
              {sortedDeals.map((deal) => {
                const checked = keep.has(deal.application_id);
                const disabled = !checked && atCap;
                return (
                  <li key={deal.application_id}>
                    <label
                      className={cn(
                        "flex cursor-pointer items-center gap-3 rounded-md border p-3 transition-colors",
                        checked
                          ? "border-foreground bg-muted/40"
                          : "border-border hover:bg-muted/30",
                        disabled && "cursor-not-allowed opacity-50"
                      )}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={disabled}
                        onCheckedChange={() => toggle(deal.application_id)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-foreground">
                          {deal.deal_name}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {deal.workspace_name}
                        </span>
                      </span>
                      {checked && (
                        <Gem className="h-3.5 w-3.5 shrink-0 text-foreground" />
                      )}
                    </label>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        )}

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Keeping {keep.size} / {newCap}
          </span>
          {demotedCount > 0 && (
            <span>
              {demotedCount} deal{demotedCount === 1 ? "" : "s"} will lose
              premium when your plan changes
            </span>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm([...keep])}
            disabled={
              isSubmitting || isLoading || isError || sortedDeals.length === 0
            }
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Downgrading…
              </>
            ) : (
              "Downgrade & keep selected"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
